import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useState } from "react";
import { useEffectOnce } from "react-use";
import { Licence, LicenceLocalization, LicenceSettings } from "@/lib/types/Licence";
import { Branch } from "@/lib/types/Branch";
import { Article } from "@/lib/types/Article";
import CookieHelper from "@/src/utils/helpers/CookieHelper";

interface IAppContext {
  licenceSettings: LicenceSettings;
  branches: Branch[];
  programs: Article[];
  toggle: boolean;
  setToggle: Dispatch<SetStateAction<boolean>>;
  mobile: boolean;
  setMobile: Dispatch<SetStateAction<boolean>>;
  smallMobileMenu: boolean;
  setSmallMobileMenu: Dispatch<SetStateAction<boolean>>;
  search: boolean;
  setSearch: Dispatch<SetStateAction<boolean>>;
  getLocalization: (key: string) => string | undefined;
  getLocalizationObj: (key: string) => LicenceLocalization;
  getThinkerWords: () => LicenceLocalization[];
}

export const AppContext = createContext<IAppContext | undefined>(undefined);

export const useAppContext = (): IAppContext => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within a Context provider");
  }
  return context;
};

interface IAppProviderProps {
  children: ReactNode;
  licence: Licence;
  branches: Branch[];
  programs: Article[];
}

const AppProvider: FC<IAppProviderProps> = ({ children, licence, branches, programs }) => {
  const [toggle, setToggle] = useState<boolean>(true);
  const [search, setSearch] = useState<boolean>(true);
  const [mobile, setMobile] = useState<boolean>(true);
  const [smallMobileMenu, setSmallMobileMenu] = useState<boolean>(true);

  const licenceSettings = {
    ...licence.LicenceSettings[0],
    SocialMediaLinks: licence.LicenceSettings[0].SocialMediaLinks.filter(s => s.EmployeeId === null)
  };
  const licenceLocalizations = licence.LicenceLocalizations;

  const getLocalization = (key: string) => {
    return licenceLocalizations.find(l => l.Key === key)?.Translations;
  };

  const getLocalizationObj = (key: string) => {
    return licenceLocalizations.find(l => l.Key === key);
  };

  const getThinkerWords = () => {
    return licenceLocalizations.filter(l => l.Key.startsWith("thinkerword"));
  };

  return (
    <AppContext.Provider
      value={{
        licenceSettings,
        branches,
        programs: programs?.sort((a, b) => a.DisplayOrder - b.DisplayOrder),
        toggle,
        setToggle,
        mobile,
        setMobile,
        smallMobileMenu,
        setSmallMobileMenu,
        search,
        setSearch,
        getLocalization,
        getLocalizationObj,
        getThinkerWords
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
